import { getSortedDuedates, getSubstanceText } from 'prove-common'
import type {
  CompactPlan,
  GroupedTaskDelegation,
  PlanStatusEnum,
  Task
} from 'prove-common'

export type TaskStatusFilter = 'incomplete' | 'completed' | 'all'
export type DeadlineFilter = 'all' | 'upcoming' | 'today' | 'overdue' | 'none'
export type ApprovalFilter = 'all' | 'approved' | 'awaiting' | 'declined'
export type PlanOverviewStatus = Extract<
  PlanStatusEnum,
  'draft' | 'active' | 'deactivated'
>
export type PlanStatusFilter = 'all' | PlanOverviewStatus

export type TaskFilterState = {
  searchText: string
  planId: string
  subPlanIds: string[]
  productIds: string[]
  matrixIds: string[]
  substanceIds: string[]
  sampntTypeIds: string[]
  planStatus: PlanStatusFilter
  deadline: DeadlineFilter
  approval: ApprovalFilter
}

export type TaskOverviewRow = {
  id: number
  task: Task
  plans: CompactPlan[]
  planFilterIds: number[]
  planChain: CompactPlan[]
  mainPlan?: CompactPlan
  subPlan?: CompactPlan
  primaryPlan?: CompactPlan
  title: string
  details: string
  searchableText: string
  productId?: number
  matrixId?: number
  substanceIds: number[]
  substanceIdStrings: string[]
  sampntTypeId?: number
  duedates: string[]
  nearestDuedate?: string
  isCompleted: boolean
  isDeactivated: boolean
}

export const getTaskTitle = (task: Task): string =>
  task.title?.trim() ||
  task.analysisType.sampleType?.matrix ||
  `Oppgave ${task.id}`

export const getTaskDetails = (task: Task): string =>
  [
    task.analysisType.sampleType?.product,
    task.analysisType.sampleType?.matrix,
    getSubstanceText(task.analysisType),
    task.sampntType.nameNo
  ]
    .filter(Boolean)
    .join(' - ')

export const getPlanFilterIds = (plans: CompactPlan[]): number[] =>
  Array.from(
    new Set(
      plans.flatMap((plan) => [plan.id, ...(plan.parents ?? []).map(Number)])
    )
  )

export const getPlanChain = (
  plan: CompactPlan | undefined,
  planById: Map<number, CompactPlan>
): CompactPlan[] => {
  if (plan == null) return []

  const chain: CompactPlan[] = []
  const visited = new Set<number>()
  let current: CompactPlan | undefined = plan

  while (current != null && !visited.has(current.id)) {
    chain.push(current)
    visited.add(current.id)
    const parentId: number | undefined = current.parents?.[0]
    current = parentId == null ? undefined : planById.get(Number(parentId))
  }

  return chain.reverse()
}

export const formatPlanChain = (
  planChain: CompactPlan[]
): string | undefined =>
  planChain.length === 0
    ? undefined
    : planChain
        .map((plan) => plan.description ?? `Plan ${plan.id}`)
        .join(' -> ')

export const getNearestDuedate = (
  duedates: string[],
  today: string
): string | undefined =>
  duedates.find((date) => date >= today) ?? duedates.at(-1)

export const getDaysUntil = (duedate: string, today: string): number => {
  const [dueYear, dueMonth, dueDay] = duedate.split('-').map(Number)
  const [todayYear, todayMonth, todayDay] = today.split('-').map(Number)
  const dueTime = Date.UTC(dueYear, dueMonth - 1, dueDay)
  const todayTime = Date.UTC(todayYear, todayMonth - 1, todayDay)

  return Math.floor((dueTime - todayTime) / (1000 * 60 * 60 * 24))
}

export const getDeadlineCategory = (
  nearestDuedate: string | undefined,
  today: string
): DeadlineFilter => {
  if (nearestDuedate == null) return 'none'
  if (nearestDuedate === today) return 'today'
  if (nearestDuedate > today) return 'upcoming'

  return 'overdue'
}

export const getDeadlineColor = (
  nearestDuedate: string | undefined,
  today: string
): 'danger' | 'warning' | 'info' | 'neutral' => {
  const category = getDeadlineCategory(nearestDuedate, today)

  if (category === 'none') return 'neutral'
  if (category === 'overdue') return 'danger'
  if (category === 'today') return 'warning'

  return 'info'
}

export const getFilterCount = (filters: TaskFilterState): number =>
  Number(filters.searchText.trim().length > 0) +
  Number(filters.planId !== 'all') +
  filters.subPlanIds.length +
  filters.productIds.length +
  filters.matrixIds.length +
  filters.substanceIds.length +
  filters.sampntTypeIds.length +
  Number(filters.planStatus !== 'all') +
  Number(filters.deadline !== 'all') +
  Number(filters.approval !== 'all')

const TASK_STATUS_FILTERS: TaskStatusFilter[] = [
  'incomplete',
  'completed',
  'all'
]
const PLAN_STATUS_FILTERS: PlanStatusFilter[] = [
  'all',
  'active',
  'draft',
  'deactivated'
]
const DEADLINE_FILTERS: DeadlineFilter[] = [
  'all',
  'upcoming',
  'today',
  'overdue',
  'none'
]
const APPROVAL_FILTERS: ApprovalFilter[] = [
  'all',
  'approved',
  'awaiting',
  'declined'
]

const parseOneOf = <T extends string>(
  allowed: readonly T[],
  value: string | null,
  fallback: T
): T => (allowed.includes(value as T) ? (value as T) : fallback)

export const getEmptyTaskFilters = (): TaskFilterState => ({
  searchText: '',
  planId: 'all',
  subPlanIds: [],
  productIds: [],
  matrixIds: [],
  substanceIds: [],
  sampntTypeIds: [],
  planStatus: 'all',
  deadline: 'all',
  approval: 'all'
})

export const TASK_URL_PARAMS = {
  tab: 'tab',
  search: 'q',
  plan: 'plan',
  subPlan: 'subPlan',
  product: 'product',
  matrix: 'matrix',
  substance: 'substance',
  sampntType: 'sampntType',
  planStatus: 'planStatus',
  deadline: 'deadline',
  approval: 'approval',
  pageIndex: 'pageIndex',
  pageSize: 'pageSize',
  sortId: 'sortId',
  sortDesc: 'sortDesc'
} as const

export const parseTaskStatusFilter = (value: string | null): TaskStatusFilter =>
  parseOneOf(TASK_STATUS_FILTERS, value, 'incomplete')

export const parseTaskFilterState = (
  params: URLSearchParams
): TaskFilterState => ({
  searchText: params.get(TASK_URL_PARAMS.search) ?? '',
  planId: params.get(TASK_URL_PARAMS.plan) ?? 'all',
  subPlanIds: params.getAll(TASK_URL_PARAMS.subPlan),
  productIds: params.getAll(TASK_URL_PARAMS.product),
  matrixIds: params.getAll(TASK_URL_PARAMS.matrix),
  substanceIds: params.getAll(TASK_URL_PARAMS.substance),
  sampntTypeIds: params.getAll(TASK_URL_PARAMS.sampntType),
  planStatus: parseOneOf(
    PLAN_STATUS_FILTERS,
    params.get(TASK_URL_PARAMS.planStatus),
    'all'
  ),
  deadline: parseOneOf(
    DEADLINE_FILTERS,
    params.get(TASK_URL_PARAMS.deadline),
    'all'
  ),
  approval: parseOneOf(
    APPROVAL_FILTERS,
    params.get(TASK_URL_PARAMS.approval),
    'all'
  )
})

export const parsePositiveInt = (
  value: string | null,
  fallback: number
): number => {
  // `Number(null)` and `Number('')` are both 0, so guard before converting.
  if (value == null || value.trim().length === 0) return fallback

  const parsed = Number(value)

  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback
}

const setOrDelete = (
  params: URLSearchParams,
  key: string,
  value: string,
  defaultValue: string
): void => {
  if (value === defaultValue) params.delete(key)
  else params.set(key, value)
}

const setAll = (
  params: URLSearchParams,
  key: string,
  values: string[]
): void => {
  params.delete(key)
  values.forEach((value) => params.append(key, value))
}

/**
 * Writes the view state onto a copy of the given params. Unknown params (such
 * as the workspace selection) are preserved, and default values are removed so
 * a pristine view keeps a clean URL.
 */
export const applyTaskViewStateToParams = (
  params: URLSearchParams,
  state: {
    filters: TaskFilterState
    selectedTab: TaskStatusFilter
    pageIndex: number
    pageSize: number
    sortId?: string
    sortDesc?: boolean
  }
): URLSearchParams => {
  const next = new URLSearchParams(params)
  const { filters } = state

  setOrDelete(next, TASK_URL_PARAMS.tab, state.selectedTab, 'incomplete')
  setOrDelete(next, TASK_URL_PARAMS.search, filters.searchText, '')
  setOrDelete(next, TASK_URL_PARAMS.plan, filters.planId, 'all')
  setOrDelete(next, TASK_URL_PARAMS.planStatus, filters.planStatus, 'all')
  setOrDelete(next, TASK_URL_PARAMS.deadline, filters.deadline, 'all')
  setOrDelete(next, TASK_URL_PARAMS.approval, filters.approval, 'all')
  setAll(next, TASK_URL_PARAMS.subPlan, filters.subPlanIds)
  setAll(next, TASK_URL_PARAMS.product, filters.productIds)
  setAll(next, TASK_URL_PARAMS.matrix, filters.matrixIds)
  setAll(next, TASK_URL_PARAMS.substance, filters.substanceIds)
  setAll(next, TASK_URL_PARAMS.sampntType, filters.sampntTypeIds)
  setOrDelete(next, TASK_URL_PARAMS.pageIndex, state.pageIndex.toString(), '0')
  setOrDelete(next, TASK_URL_PARAMS.pageSize, state.pageSize.toString(), '20')

  if (state.sortId == null) {
    next.delete(TASK_URL_PARAMS.sortId)
    next.delete(TASK_URL_PARAMS.sortDesc)
  } else {
    next.set(TASK_URL_PARAMS.sortId, state.sortId)
    next.set(TASK_URL_PARAMS.sortDesc, String(state.sortDesc === true))
  }

  return next
}

export const getSortValue = (row: TaskOverviewRow, today: string): number => {
  if (row.nearestDuedate == null) return Number.MAX_SAFE_INTEGER
  const daysUntil = getDaysUntil(row.nearestDuedate, today)

  if (daysUntil >= 0) return daysUntil

  return 10_000 + Math.abs(daysUntil)
}

export const getUpcomingDeadlineRows = (
  rows: TaskOverviewRow[],
  today: string,
  limit = 6,
  daysAhead = 30
): TaskOverviewRow[] =>
  rows
    .filter(
      (row) =>
        !row.isCompleted &&
        !row.isDeactivated &&
        row.nearestDuedate != null &&
        row.nearestDuedate >= today &&
        getDaysUntil(row.nearestDuedate, today) <= daysAhead
    )
    .sort((a, b) => getSortValue(a, today) - getSortValue(b, today))
    .slice(0, limit)

/**
 * Overdue tasks are deliberately independent of the "days ahead" horizon: a
 * missed deadline stays relevant no matter which period the user is looking at.
 * Most overdue (oldest deadline) is listed first.
 */
export const getOverdueDeadlineRows = (
  rows: TaskOverviewRow[],
  today: string,
  limit = 6
): TaskOverviewRow[] =>
  rows
    .filter(
      (row) =>
        !row.isCompleted &&
        !row.isDeactivated &&
        row.nearestDuedate != null &&
        row.nearestDuedate < today
    )
    .sort((a, b) =>
      (a.nearestDuedate ?? '').localeCompare(b.nearestDuedate ?? '')
    )
    .slice(0, limit)

/**
 * Completion ratio, used to sort the "Prøver tatt" column. Sorting by the raw
 * count would rank a 1/1 task below a 2/50 task, which is not what the progress
 * ring communicates.
 */
export const getCompletionRatio = (row: TaskOverviewRow): number => {
  const quantity = row.task.quantity ?? 0
  if (quantity <= 0) return 0

  return Math.min((row.task.totalCount ?? 0) / quantity, 1)
}

/**
 * Ranks approval status by how much attention it needs, so that sorting
 * ascending surfaces problems first instead of ordering alphabetically.
 */
export const getApprovalSortRank = (row: TaskOverviewRow): number => {
  const status = row.task.analysisTypeApprovalStatus

  if (status === 'declined') return 0
  if (status === 'awaiting') return 1
  if (status === 'approved') return 2

  return 3
}

export const matchesFilters = (
  row: TaskOverviewRow,
  filters: TaskFilterState,
  today: string
): boolean => {
  const searchText = filters.searchText.trim().toLocaleLowerCase('nb-NO')

  const planMatches =
    filters.subPlanIds.length > 0
      ? filters.subPlanIds.some((planId) =>
          row.planFilterIds.includes(Number(planId))
        )
      : filters.planId === 'all' ||
        row.planFilterIds.includes(Number(filters.planId))
  const planStatusMatches =
    filters.planStatus === 'all' ||
    row.plans.some((plan) => plan.status === filters.planStatus)
  const productMatches =
    filters.productIds.length === 0 ||
    filters.productIds.includes(row.productId?.toString() ?? '')
  const matrixMatches =
    filters.matrixIds.length === 0 ||
    filters.matrixIds.includes(row.matrixId?.toString() ?? '')
  const sampntTypeMatches =
    filters.sampntTypeIds.length === 0 ||
    filters.sampntTypeIds.includes(row.sampntTypeId?.toString() ?? '')
  const substanceMatches = filters.substanceIds.every((substanceId) =>
    row.substanceIdStrings.includes(substanceId)
  )
  const deadlineCategory = getDeadlineCategory(row.nearestDuedate, today)
  const deadlineMatches =
    filters.deadline === 'all' || deadlineCategory === filters.deadline
  const approvalMatches =
    filters.approval === 'all' ||
    row.task.analysisTypeApprovalStatus === filters.approval

  return (
    (searchText.length === 0 || row.searchableText.includes(searchText)) &&
    planMatches &&
    planStatusMatches &&
    productMatches &&
    matrixMatches &&
    sampntTypeMatches &&
    substanceMatches &&
    deadlineMatches &&
    approvalMatches
  )
}

export const getVisibleRows = (
  selectedTab: TaskStatusFilter,
  filteredRows: TaskOverviewRow[],
  incompleteRows: TaskOverviewRow[],
  completedRows: TaskOverviewRow[]
): TaskOverviewRow[] => {
  if (selectedTab === 'incomplete') return incompleteRows
  if (selectedTab === 'completed') return completedRows

  return filteredRows
}

/**
 * Denormalises the flat `/tasks` response into the rows the overview screens
 * work with, resolving each task's plan chain and deadline state. Sub-tasks
 * (those with a `parentTaskId`) and tasks whose plans are not loaded are
 * dropped. Rows come back sorted by deadline urgency.
 */
export const buildTaskOverviewRows = (
  tasks: Task[] | undefined,
  planById: Map<number, CompactPlan>,
  today: string
): TaskOverviewRow[] => {
  if (tasks == null) return []

  return tasks
    .filter((task) => task.parentTaskId == null)
    .flatMap((task): TaskOverviewRow[] => {
      const taskPlans = (task.planIds ?? [])
        .map((planId) => planById.get(planId))
        .filter((plan): plan is CompactPlan => plan != null)

      if (taskPlans.length === 0) return []

      const primaryPlan = taskPlans[0]
      const planChain = getPlanChain(primaryPlan, planById)
      const plansForFiltering = planChain.length > 0 ? planChain : taskPlans
      const mainPlan = planChain[0] ?? primaryPlan
      const subPlan = planChain.at(-1) ?? primaryPlan
      const duedates = getSortedDuedates(task.duedates)
      const nearestDuedate = getNearestDuedate(duedates, today)
      const title = getTaskTitle(task)
      const details = getTaskDetails(task)
      const planText =
        formatPlanChain(planChain) ??
        taskPlans.map((plan) => plan.description ?? `Plan ${plan.id}`).join(' ')
      const substanceIds =
        task.analysisType.substances?.map(({ substance }) => substance.id) ?? []

      return [
        {
          id: task.id,
          task,
          plans: plansForFiltering,
          planFilterIds: getPlanFilterIds(plansForFiltering),
          planChain,
          mainPlan,
          subPlan,
          primaryPlan,
          title,
          details,
          searchableText: [title, details, planText]
            .join(' ')
            .toLocaleLowerCase('nb-NO'),
          productId: task.analysisType.sampleType?.productId,
          matrixId: task.analysisType.sampleType?.matrixId,
          substanceIds,
          substanceIdStrings: substanceIds.map(String),
          sampntTypeId: task.sampntType.id,
          duedates,
          nearestDuedate,
          isCompleted:
            task.quantity != null &&
            task.totalCount != null &&
            task.totalCount >= task.quantity,
          isDeactivated: plansForFiltering.some(
            (plan) => plan.status === 'deactivated'
          )
        }
      ]
    })
    .sort((a, b) => getSortValue(a, today) - getSortValue(b, today))
}

/**
 * Restricts rows to the plans belonging to a workspace. A task can sit in
 * several plans, so membership is a set intersection rather than a lookup.
 */
export const filterRowsByPlanIds = (
  rows: TaskOverviewRow[],
  planIds: Set<number> | undefined
): TaskOverviewRow[] =>
  planIds == null
    ? rows
    : rows.filter((row) =>
        row.planFilterIds.some((planId) => planIds.has(planId))
      )

export type WorkspaceTaskSummary = {
  total: number
  completed: number
  incomplete: number
  overdue: number
  nextDuedate?: string
}

/**
 * Summary shown on the workspace and tilsynsdivisjon overview screens. Counts
 * every plan status the task overview loads, so the numbers always agree with
 * the tasks screen the card links to.
 */
export const getWorkspaceTaskSummary = (
  rows: TaskOverviewRow[],
  planIds: Set<number> | undefined,
  today: string
): WorkspaceTaskSummary => {
  const scopedRows = filterRowsByPlanIds(rows, planIds)
  const completed = scopedRows.filter((row) => row.isCompleted).length
  const overdue = getOverdueDeadlineRows(
    scopedRows,
    today,
    Number.MAX_SAFE_INTEGER
  ).length
  const nextDuedate = getUpcomingDeadlineRows(
    scopedRows,
    today,
    1,
    Number.MAX_SAFE_INTEGER
  )[0]?.nearestDuedate

  return {
    total: scopedRows.length,
    completed,
    incomplete: scopedRows.length - completed,
    overdue,
    nextDuedate
  }
}

export interface DelegationNode extends GroupedTaskDelegation {
  subDelegations: DelegationNode[]
  samplesDelegated: number // sum of subDelegations' quantities
}

// Build a delegation tree with a single root. Returns undefined if no delegations.
// Throws error if multiple roots detected (data integrity issue).
export const buildDelegationTree = (
  delegations: GroupedTaskDelegation[] = []
): DelegationNode | undefined => {
  if (delegations.length === 0) return undefined
  const byId: Record<number, DelegationNode> = {}
  for (const d of delegations) {
    byId[d.id] = { ...d, subDelegations: [], samplesDelegated: 0 }
  }
  const roots: DelegationNode[] = []
  for (const d of delegations) {
    const node = byId[d.id]
    if (
      d.parentDelegationId == null ||
      d.parentDelegationId === d.id ||
      byId[d.parentDelegationId] == null
    ) {
      roots.push(node)
    } else {
      byId[d.parentDelegationId].subDelegations.push(node)
    }
  }
  if (roots.length === 0) return undefined
  if (roots.length > 1) {
    throw new Error(
      'Multiple root delegations found; only one root is supported.'
    )
  }
  const root = roots[0]
  // Compute samplesDelegated for each node (sum of direct child quantities)
  const computeSamplesDelegated = (node: DelegationNode): void => {
    node.samplesDelegated = node.subDelegations.reduce(
      (sum, child) => sum + (child.quantity ?? 0),
      0
    )
    node.subDelegations.forEach(computeSamplesDelegated)
  }
  computeSamplesDelegated(root)
  return root
}
